import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.ts';
import { useModosithat, useTorolhet, useUjFelvetel } from '../felhasznalo-context.ts';
import { Mezo } from './Mezo.tsx';

/**
 * Technikai partnerek. (3.0 / 7.)
 *
 * Átvevő telephelyek és fuvarozók — nem éttermek, a járatlapon nem
 * jelennek meg. A kiadásnál választjuk ki őket, és az adataik az átadási
 * okmányokra, illetve a fenntarthatósági nyilatkozatra kerülnek.
 *
 * Az utolsó négy mező (kapcsolattartó, lerakodás címe, távolság,
 * szerződésszám) kifejezetten a nyilatkozathoz kell. **Ha a lerakodás címe
 * üres, a partner sima címe kerül a nyilatkozatra.**
 */

interface TechSor {
  id: number;
  szerep: string;
  nev: string;
  cegnev: string | null;
  adoszam: string | null;
  szekhely: string | null;
  cim: string | null;
  ktj: string | null;
  tel: string | null;
  email: string | null;
  kapcsolattarto: string | null;
  lerakodas_cim: string | null;
  tavolsag_km: number | null;
  szerzodes_szam: string | null;
  tipus: string | null;
  aktiv: boolean;
  megjegyzes: string | null;
}

interface TechValasz {
  sorok: TechSor[];
  szerepek: string[];
  tipusok: string[];
}

const SZEREP_NEVEK: Record<string, string> = {
  atvevo: 'Átvevő telephely',
  fuvarozo: 'Szállító (fuvarozó)',
};

const TIPUS_NEVEK: Record<string, string> = {
  HSO: 'HSO',
  MO: 'Moslék',
  mindketto: 'Mindkettő',
};

export function TechnikaiPartnerek({ fejlec }: { fejlec?: React.ReactNode }) {
  const [adat, setAdat] = useState<TechValasz | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);
  const [ujNev, setUjNev] = useState('');
  const [ujSzerep, setUjSzerep] = useState('atvevo');
  const modosithat = useModosithat('torzs');
  const ujFelvetel = useUjFelvetel('torzs');
  const torolhet = useTorolhet();

  const betolt = useCallback(async () => {
    try {
      setAdat(await api.get<TechValasz>('/api/technikai-partner'));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A partnereket nem sikerült betölteni.');
    }
  }, []);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  async function hivas(mit: () => Promise<TechValasz>) {
    try {
      setAdat(await mit());
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A művelet nem sikerült.');
    }
  }

  const mezo = (s: TechSor, k: keyof TechSor, osztaly = 'txt') => (
    <Mezo
      ertek={(s[k] as string | number | null) ?? ''}
      osztaly={osztaly}
      tiltva={!modosithat}
      onMent={async (uj) => {
        await hivas(() => api.patch<TechValasz>(`/api/technikai-partner/${s.id}/${k}`, { ertek: uj }));
      }}
    />
  );

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Partnerek</span>
        {fejlec}
        <span className="hint">
          Átvevő telephelyek és fuvarozók. A járatlapon nem jelennek meg — a kiadásnál és az
          átadási okmányokon szerepelnek.
        </span>
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}

      {ujFelvetel && (
        <div className="actbar">
          <select value={ujSzerep} onChange={(e) => setUjSzerep(e.target.value)}>
            {(adat?.szerepek ?? []).map((sz) => (
              <option key={sz} value={sz}>
                {SZEREP_NEVEK[sz] ?? sz}
              </option>
            ))}
          </select>
          <input
            value={ujNev}
            placeholder="megnevezés"
            style={{ minWidth: 220 }}
            onChange={(e) => setUjNev(e.target.value)}
          />
          <button
            className="btn sm pri"
            disabled={!ujNev.trim()}
            onClick={async () => {
              await hivas(() =>
                api.post<TechValasz>('/api/technikai-partner', { szerep: ujSzerep, nev: ujNev.trim() }),
              );
              setUjNev('');
            }}
          >
            + Felvétel
          </button>
          <span className="hint">
            A többi adatot a táblázatban töltsd ki — a mentés mezőnként történik.
          </span>
        </div>
      )}

      <div className="matrix" style={{ maxHeight: 620, overflow: 'auto' }}>
        <table className="tt2">
          <thead>
            <tr>
              <th>Szerep</th>
              <th>Megnevezés</th>
              <th>Cégnév</th>
              <th className="n">Adószám</th>
              <th>Székhely</th>
              <th>Cím</th>
              <th className="n">KTJ</th>
              <th className="n">Telefon</th>
              <th>E-mail</th>
              <th>Kapcsolattartó</th>
              <th>Lerakodás címe</th>
              <th className="n">Táv. (km)</th>
              <th className="n">Szerz. szám</th>
              <th>Átvesz</th>
              <th>Megjegyzés</th>
              <th className="n">Aktív</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(adat?.sorok ?? []).map((s) => (
              <tr key={s.id} className={s.aktiv ? undefined : 'nul'}>
                <td className="sel">
                  <select
                    value={s.szerep}
                    disabled={!modosithat}
                    onChange={(e) =>
                      void hivas(() =>
                        api.patch<TechValasz>(`/api/technikai-partner/${s.id}/szerep`, {
                          ertek: e.target.value,
                        }),
                      )
                    }
                  >
                    {(adat?.szerepek ?? []).map((sz) => (
                      <option key={sz} value={sz}>
                        {SZEREP_NEVEK[sz] ?? sz}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{mezo(s, 'nev')}</td>
                <td>{mezo(s, 'cegnev')}</td>
                <td>{mezo(s, 'adoszam', 'c')}</td>
                <td>{mezo(s, 'szekhely')}</td>
                <td>{mezo(s, 'cim')}</td>
                <td>{mezo(s, 'ktj', 'c')}</td>
                <td>{mezo(s, 'tel', 'c')}</td>
                <td>{mezo(s, 'email')}</td>
                <td>{mezo(s, 'kapcsolattarto')}</td>
                <td>{mezo(s, 'lerakodas_cim')}</td>
                <td>{mezo(s, 'tavolsag_km', 'c')}</td>
                <td>{mezo(s, 'szerzodes_szam', 'c')}</td>
                <td className="sel">
                  <select
                    value={s.tipus ?? ''}
                    disabled={!modosithat || s.szerep !== 'atvevo'}
                    onChange={(e) =>
                      void hivas(() =>
                        api.patch<TechValasz>(`/api/technikai-partner/${s.id}/tipus`, {
                          ertek: e.target.value,
                        }),
                      )
                    }
                  >
                    <option value="">—</option>
                    {(adat?.tipusok ?? []).map((t) => (
                      <option key={t} value={t}>
                        {TIPUS_NEVEK[t] ?? t}
                      </option>
                    ))}
                  </select>
                </td>
                <td>{mezo(s, 'megjegyzes')}</td>
                <td className="c">
                  <input
                    type="checkbox"
                    checked={s.aktiv}
                    disabled={!modosithat}
                    onChange={(e) =>
                      void hivas(() =>
                        api.patch<TechValasz>(`/api/technikai-partner/${s.id}/aktiv`, {
                          ertek: e.target.checked,
                        }),
                      )
                    }
                  />
                </td>
                <td className="act">
                  {torolhet && (
                    <button
                      className="btn sm torlo"
                      onClick={() =>
                        void hivas(() => api.torol<TechValasz>(`/api/technikai-partner/${s.id}`))
                      }
                    >
                      Töröl
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {(adat?.sorok.length ?? 0) === 0 && (
              <tr>
                <td colSpan={17} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  Még nincs átvevő telephely vagy fuvarozó felvéve.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <span className="hint">
        A kapcsolattartó, a lerakodás címe, a távolság és a szerződésszám a fenntarthatósági
        nyilatkozathoz kell. Ha a lerakodás címe üres, a partner sima címe kerül a nyilatkozatra.
      </span>
    </>
  );
}
